import {
  forwardRef,
  type MouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useAnnounce } from "@/hooks/useAnnounce";
import { cn } from "@/lib/utils";
import debounce from "@/utils/debounce";

export type QuantitySelectorRef = {
  flush: () => Promise<void>;
};

type DebouncedFn = {
  (newQty: number): Promise<void> | void;
  cancel: () => void;
  flush: () => Promise<void> | undefined;
};

type QuantitySelectorProps = {
  quantity: number;
  onChange: (newQuantity: number) => Promise<void> | void;
  onRemove?: () => Promise<void> | void;
  maxQuantity?: number;
  debounceMs?: number;
  className?: string;
  disabled?: boolean;
  announceChanges?: boolean;
  formatQuantityAnnouncement?: (quantity: number) => string;
  itemName?: string;
  onInteractionStart?: ((target: HTMLButtonElement) => void) | undefined;
};

const QuantitySelector = forwardRef<QuantitySelectorRef, QuantitySelectorProps>(
  function QuantitySelector(
    {
      quantity,
      onChange,
      onRemove,
      maxQuantity = 0,
      debounceMs = 0,
      className,
      disabled = false,
      announceChanges = true,
      formatQuantityAnnouncement,
      itemName,
      onInteractionStart,
    },
    ref,
  ) {
    const announce = useAnnounce();
    const [localQuantity, setLocalQuantity] = useState(quantity);
    const [loading, setLoading] = useState(false);
    const pendingRef = useRef(false);

    // Re-sync once the optimistic update has settled. Without `loading` here,
    // a prop change that arrives while `pendingRef` is true can be skipped
    // forever until the component remounts.
    useEffect(() => {
      if (!pendingRef.current && !loading) {
        setLocalQuantity(quantity);
      }
    }, [loading, quantity]);

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const callOnChange = useCallback(async (newQty: number) => {
      setLoading(true);
      try {
        await onChangeRef.current(newQty);
      } finally {
        pendingRef.current = false;
        setLoading(false);
      }
    }, []);

    const debouncedOnChange = useRef<DebouncedFn | null>(null);

    useEffect(() => {
      if (debounceMs > 0) {
        debouncedOnChange.current = debounce(
          (newQty: number) => callOnChange(newQty),
          debounceMs,
          false,
        ) as DebouncedFn;
      } else {
        debouncedOnChange.current = null;
      }
      return () => {
        debouncedOnChange.current?.cancel?.();
      };
    }, [debounceMs, callOnChange]);

    useImperativeHandle(ref, () => ({
      flush: async () => {
        const result = debouncedOnChange.current?.flush?.();
        if (result) await result;
      },
    }));

    const fire = useCallback(
      (newQty: number) => {
        pendingRef.current = true;
        setLocalQuantity(newQty);
        if (announceChanges) {
          announce(
            formatQuantityAnnouncement?.(newQty) ?? `Quantity ${newQty}`,
          );
        }
        if (debouncedOnChange.current) {
          debouncedOnChange.current(newQty);
        } else {
          callOnChange(newQty);
        }
      },
      [announce, announceChanges, callOnChange, formatQuantityAnnouncement],
    );

    const handleDecrement = useCallback(
      async (event: MouseEvent<HTMLButtonElement>) => {
        onInteractionStart?.(event.currentTarget);
        if (localQuantity <= 1) {
          setLoading(true);
          try {
            await onRemove?.();
          } finally {
            setLoading(false);
          }
          return;
        }
        fire(localQuantity - 1);
      },
      [fire, localQuantity, onInteractionStart, onRemove],
    );

    const handleIncrement = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (maxQuantity > 0 && localQuantity >= maxQuantity) return;
        onInteractionStart?.(event.currentTarget);
        fire(localQuantity + 1);
      },
      [fire, localQuantity, maxQuantity, onInteractionStart],
    );

    return (
      <div className={cn("flex items-center gap-4", className)}>
        <Button
          variant="outline"
          size="icon-sm"
          onClick={handleDecrement}
          className="rounded-lg border-[#dbdbdb] !bg-transparent shadow-none hover:!bg-[#faf9f7]"
          disabled={disabled || loading}
          aria-label={
            itemName ? `Decrease quantity of ${itemName}` : "Decrease quantity"
          }
        >
          <span className="font-trimMono text-[16px] leading-[1.6] tracking-[0.48px] text-[#1f2022]">
            -
          </span>
        </Button>
        <span className="relative inline-flex items-center justify-center">
          <span
            className={cn(
              "font-trimMono text-[16px] tracking-[0.48px] text-[#1f2022]",
              loading && "invisible",
            )}
          >
            {localQuantity}
          </span>
          {loading && (
            <Spinner
              aria-hidden="true"
              className="absolute size-4 text-[#1f2022] md:size-5"
            />
          )}
        </span>
        <Button
          variant="outline"
          size="icon-sm"
          onClick={handleIncrement}
          className="rounded-lg border-[#dbdbdb] !bg-transparent shadow-none hover:!bg-[#faf9f7]"
          disabled={
            disabled ||
            loading ||
            (maxQuantity > 0 && localQuantity >= maxQuantity)
          }
          aria-label={
            itemName ? `Increase quantity of ${itemName}` : "Increase quantity"
          }
        >
          <span className="font-trimMono text-[16px] leading-[1.6] tracking-[0.48px] text-[#1f2022]">
            +
          </span>
        </Button>
      </div>
    );
  },
);

export default QuantitySelector;
